import React, { HTMLProps, useContext, useState } from 'react';
import { useHistory } from 'react-router';
import { createFundsSelectionContext } from '../../../contexts/funds-robot/FundsSelectionContext';
import { Asset } from '../../../types/Asset';
import { Button } from '../../general/Button';
import { FundsSelectionSticky } from './FundsSelectionSticky';
import { useTranslation } from 'react-i18next';
import { LoadingStateWrapper } from '../../general/LoadingStateWrapper';
import { useGetLatestRate } from '../../../api/funds-robot/useGetLatestRate';
import { toPercentage, toPrice } from '../../../utils/parsers';
import { useGetYearlyReturnsModelPortfolio } from '../../../api/funds-robot/useGetYearlyReturnsModelPortfolio';
import { useGetYearlyReturnsFund } from '../../../api/funds-robot/useGetYearlyReturnsFund';
import { FundsQueryContext } from '../../../contexts/funds-robot/FundsQueryContext';
import { DateTime } from 'luxon';
import { LoadingSpinner } from '../../general/LoadingSpinner';
import { IconElements } from '../../../config/icons';

export const IntermediateFundsSelectionContext = createFundsSelectionContext();

const fundCategories = ['Portfolio', 'Stock', 'Bond'] as const;
type FundCategory = typeof fundCategories[number];

export const FundsSelectionScreen: React.FC<
    {
        sections?: { fundCategory: FundCategory; funds: Asset[] }[];
        titleBox?: React.ReactNode;
        areTitlesWhite?: boolean;
    } & HTMLProps<HTMLDivElement>
> = ({ sections = [], titleBox, areTitlesWhite = false, className, ref }) => {
    const fundsQueryContext = useContext(FundsQueryContext);
    const fundsContext = useContext(IntermediateFundsSelectionContext);
    const [activeTab, setActiveTab] = useState<FundCategory>(
        fundsContext.funds.selected.length > 0 ? fundsContext.funds.selected[0].category : 'Portfolio',
    );

    const { isLoading, isFetching, isError, isIdle } = fundsQueryContext.funds.query;
    return (
        <LoadingStateWrapper
            isLoading={isLoading}
            isFetching={isFetching}
            isError={isError}
            isIdle={isIdle}
            className={'w-full'}
        >
            <div
                id="funds-selection-screen"
                ref={ref}
                className={`flex flex-col items-center justify-center ${className}`}
            >
                <div className="flex flex-col items-center max-w-screen-2xl w-full">
                    {!!titleBox && titleBox}

                    <div
                        className={`max-w-screen-lg w-full mx-auto px-4 py-4 sm:py-16 flex-1 self-stretch ${
                            !!titleBox ? 'mt-8' : ''
                        }`}
                    >
                        <div
                            className={
                                'grid grid-cols-3 items-center justify-between w-full px-0 sm:px-4 gap-4 md:gap-20'
                            }
                        >
                            {fundCategories.map((fundCategory) => {
                                return (
                                    <TabButton
                                        fundCategory={fundCategory}
                                        onClick={() => {
                                            if (activeTab !== fundCategory) {
                                                setActiveTab(fundCategory);
                                                fundsContext.funds.removeAll();
                                            }
                                        }}
                                        isActive={activeTab === fundCategory}
                                        areTitlesWhite={areTitlesWhite}
                                        key={fundCategory}
                                    />
                                );
                            })}
                        </div>
                        {sections
                            .filter(({ fundCategory }) => fundCategory === activeTab)
                            .map((section, index) => (
                                <FundSection funds={section.funds} areTitlesWhite={areTitlesWhite} key={index} />
                            ))}
                    </div>

                    <FundsSelectionSticky />
                    <DisclaimerSection />
                </div>
            </div>
        </LoadingStateWrapper>
    );
};

export const DisclaimerSection: React.FC<
    {
        onFundDetailsScreen?: boolean;
    } & HTMLProps<HTMLDivElement>
> = ({ onFundDetailsScreen = false }) => {
    const { t } = useTranslation('funds');

    const [isCollapsed, setIsCollapsed] = useState(!onFundDetailsScreen);

    const disclaimerTitle = (
        <div className="flex flex-row items-baseline">
            <span className="font-medium text-base lg:text-lg">{t('funds-comparison-screen-disclaimer-title')}</span>
        </div>
    );

    return (
        <div
            className={`${isCollapsed ? 'bg-white py-4 rounded-lg shadow-lg z-100' : ''} ${
                onFundDetailsScreen ? 'mt-8' : ''
            }`}
        >
            <div>
                <div
                    className="flex flex-col items-baseline px-4 max-w-screen-lg"
                    onClick={() => {
                        if (onFundDetailsScreen) setIsCollapsed(!isCollapsed);
                    }}
                >
                    {!isCollapsed && (
                        <div className={`mx-auto`}>
                            {disclaimerTitle}
                            <div className="mt-2 font-base text-sm">
                                <span>{t('funds-comparison-screen-disclaimer-text-short')}</span>
                                <span className="text-bnd-green underline cursor-pointer">Lees meer</span>
                            </div>
                        </div>
                    )}
                    {isCollapsed && (
                        <>
                            {disclaimerTitle}
                            <div className="mt-2 font-base text-sm">
                                <span>{t('funds-comparison-screen-disclaimer-text')}</span>
                                {onFundDetailsScreen && (
                                    <span className="text-bnd-green underline cursor-pointer">Sluiten</span>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// This is implemented this way since there was no backend availability for BND. Ideally the backend provides whether a fund is sustainable
const sustainableFunds = ['20', '21', '22', '23', '24', '8015', '8016'];

const FundSection: React.FC<{ funds?: Asset[]; areTitlesWhite: boolean } & HTMLProps<HTMLDivElement>> = ({
    funds = [],
    areTitlesWhite = false,
}) => {
    const { t } = useTranslation('funds');
    return (
        <div className="mt-16">
            <div className={`flex flex-col md:flex-row px-8 items-center ${areTitlesWhite ? 'text-white' : ''}`}>
                <div className="" style={{ flex: 2 }}>
                    <div className="font-medium truncate">
                        <span className="">{t('funds-selection-screen-name')}</span>
                    </div>
                </div>
                {funds.filter((f) => f.category !== 'Portfolio').length > 0 && (
                    <>
                        <div className="hidden lg:block text-center" style={{ flex: 1 }}>
                            <span className="mx-2">{t('funds-selection-screen-table-label-risk')}</span>
                        </div>
                        <div className="hidden md:block text-center truncate" style={{ flex: 1 }}>
                            <span className="mx-2">{t('funds-selection-screen-table-label-rate')}</span>
                        </div>
                    </>
                )}
                <div className="flex w-full items-center flex-col sm:items-center text-center" style={{ flex: 1 }}>
                    <div className=" sm:pr-0">
                        <div className={'font-medium'}>{t('funds-selection-screen-table-label-yield')}</div>
                        <div className=" text-sm text-gray-400 ">
                            {DateTime.now().year} / {t('funds-selection-screen-table-label-yield-annual')}
                        </div>
                    </div>
                </div>
                <div style={{ flex: 2 }}></div>
            </div>
            {funds.map((item, index) => {
                if (!sustainableFunds.includes(item.id)) return <FundRow fund={item} className={'mt-4'} key={index} />;
                return null;
            })}
            <div className={'bg-transparent h-12'} />
            {funds.map((item, index) => {
                if (sustainableFunds.includes(item.id))
                    return <FundRow fund={item} className={'mt-4'} key={index} isGreen={true} />;
                return null;
            })}
            {funds.length === 0 && (
                <div className="mt-8 flex flex-row justify-center items-center">
                    <LoadingSpinner className="my-16" />
                </div>
            )}
        </div>
    );
};

const FundRow: React.FC<{ fund: Asset; isGreen?: boolean } & HTMLProps<HTMLDivElement>> = ({
    fund,
    className,
    isGreen = false,
}) => {
    const fundsContext = useContext(IntermediateFundsSelectionContext);
    const history = useHistory();
    const { t } = useTranslation('funds');

    const rates = useGetLatestRate(fund.category !== 'Portfolio' ? fund.id : undefined);
    const yearStatsModelPortfolio = useGetYearlyReturnsModelPortfolio(
        DateTime.fromISO(fund.basicCharacteristics.startDate),
        fund.category === 'Portfolio' ? fund.id : undefined,
    );
    const yearStatsFund = useGetYearlyReturnsFund(
        DateTime.fromISO(fund.basicCharacteristics.startDate),
        fund.category !== 'Portfolio' ? fund.id : undefined,
        fund.category !== 'Portfolio' ? fund.dividendPayments : undefined,
    );

    const latestRate = (rates.data?.rates[0].nav || 0) * 100;

    const ReturnRatesOverviewValues = () => {
        const thisYearReturnRates = yearStatsFund.thisYear ? toPercentage(yearStatsFund.thisYear) : '...';
        const annualReturnRates = yearStatsFund.annual ? toPercentage(yearStatsFund.annual) : '...';
        return (
            <>
                {thisYearReturnRates} / {annualReturnRates}
            </>
        );
    };

    const ReturnRatesModelOverviewValues = () => {
        const thisYearReturnRates = yearStatsModelPortfolio.thisYear
            ? toPercentage(yearStatsModelPortfolio.thisYear)
            : '...';
        const annualReturnRates = yearStatsModelPortfolio.annual ? toPercentage(yearStatsModelPortfolio.annual) : '...';
        return (
            <>
                {thisYearReturnRates} / {annualReturnRates}
            </>
        );
    };

    return (
        <div
            data-fund-id={fund.id}
            data-fund-category={fund.category}
            className={`fund-row flex flex-row items-center px-8 rounded-md shadow-xl transition ${
                fundsContext.isSelectable(fund) ? 'hover:bg-bnd-black-10 cursor-pointer' : 'opacity-50'
            }
            ${
                fundsContext.isSelected(fund)
                    ? 'bg-bnd-black-10 border-bnd-green text-bnd-green opacity-100 cursor-pointer'
                    : 'bg-white'
            }
            ${className}`}
            onClick={() => {
                if (!fundsContext.isSelectable(fund) && !fundsContext.isSelected(fund)) return;
                !fundsContext.isSelected(fund) ? fundsContext.funds.add(fund) : fundsContext.funds.remove(fund);
            }}
        >
            <div
                className="text-center sm:text-left my-4 flex flex-col"
                style={{ flex: 2, display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 3 }}
            >
                <div className={'items-center justify-center sm:justify-between flex flex-row'}>
                    <span>{fund.name}</span>
                    {isGreen && (
                        <IconElements.SustainableIcon
                            className={'h-8 w-8 text-bnd-green justify-self-end hidden sm:block'}
                        />
                    )}
                </div>
                {fund.category === 'Portfolio' ? (
                    <div className="block sm:hidden" style={{ flex: 1 }}>
                        <ReturnRatesModelOverviewValues />
                    </div>
                ) : (
                    <div className="block sm:hidden">
                        <ReturnRatesOverviewValues />
                    </div>
                )}
            </div>
            {isGreen && (
                <IconElements.SustainableIcon
                    className={'h-8 w-8 text-bnd-green justify-self-end sm:justify-self-auto sm:hidden'}
                />
            )}
            {fund.category !== 'Portfolio' && (
                <>
                    <div className="hidden lg:flex items-center justify-center" style={{ flex: 1 }}>
                        <div
                            className={`flex items-center justify-center border transition ${
                                fundsContext.isSelected(fund) ? 'border-bnd-green' : 'border-bnd-black'
                            } rounded-full w-8 h-8`}
                        >
                            <span className="block leading-2">{fund.riskCharacteristics.classification}</span>
                        </div>
                    </div>
                    <div className="hidden md:block text-center" style={{ flex: 1 }}>
                        {toPrice(latestRate)}
                    </div>
                    <div className="text-center hidden sm:block" style={{ flex: 1 }}>
                        <ReturnRatesOverviewValues />
                    </div>
                </>
            )}
            {fund.category === 'Portfolio' && (
                <div className="text-center hidden sm:block" style={{ flex: 1 }}>
                    <ReturnRatesModelOverviewValues />
                </div>
            )}

            <div
                className={`hidden sm:flex flex-row justify-end transition-opacity ${
                    fundsContext.isSelectable(fund) || fundsContext.isSelected(fund) ? 'opacity-100' : 'opacity-0'
                }`}
                style={{ flex: 2 }}
            >
                <Button
                    id={`compare-fund`}
                    data-fund-id={fund.id}
                    className={`px-8 my-4 inline-block`}
                    buttonType="internalLinkGreen"
                    isDisabled={!fundsContext.isSelectable(fund) && !fundsContext.isSelected(fund)}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (!fundsContext.isSelectable(fund) && !fundsContext.isSelected(fund)) return;
                        !fundsContext.isSelected(fund) ? fundsContext.funds.add(fund) : fundsContext.funds.remove(fund);
                    }}
                >
                    {t('funds-selection-screen-compare-button')}
                </Button>
                <Button
                    id={`show-fund`}
                    data-fund-id={fund.id}
                    buttonType="primary"
                    className="px-8 ml-4 my-4 inline-block"
                    isDisabled={!fundsContext.isSelectable(fund) && !fundsContext.isSelected(fund)}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (!fundsContext.isSelectable(fund)) return;
                        fundsContext.funds.set([fund]);
                        history.push('/comparison');
                    }}
                >
                    {t('funds-selection-screen-select-button')}
                </Button>
            </div>
        </div>
    );
};

export const TabButton: React.FC<
    {
        fundCategory: FundCategory;
        onClick: () => void;
        isActive: boolean;
        areTitlesWhite?: boolean;
    } & HTMLProps<HTMLDivElement>
> = ({ onClick, isActive = false, areTitlesWhite = false, fundCategory }) => {
    const { t } = useTranslation('funds');

    return (
        <div
            className={` ${
                isActive ? (areTitlesWhite ? 'text-white' : '') : 'text-bnd-black-40'
            } font-medium text-base sm:text-xl cursor-pointer group overflow-hidden space-y-2 ${
                isActive ? '' : areTitlesWhite ? 'hover:text-bnd-black-10' : 'hover:text-bnd-black-60'
            } ${isActive ? 'w-full' : 'w-min hover:w-full'}`}
            onClick={onClick}
            data-test-id={fundCategory}
        >
            <span>{t(fundCategoryToTranslationString(fundCategory))}</span>
            <div
                className={`h-1.5 ${isActive ? 'bg-bnd-green' : 'bg-bnd-black-40'} ${
                    isActive ? '' : 'group-hover:bg-bnd-green-hover'
                }`}
            ></div>
        </div>
    );
};

const fundCategoryToTranslationString = (fundCategory: FundCategory) => {
    switch (fundCategory) {
        case 'Portfolio':
            return 'funds-selection-screen-portfolio-section-title';
        case 'Bond':
            return 'funds-selection-screen-bond-section-title';
        case 'Stock':
            return 'funds-selection-screen-stock-section-title';
    }
};
